/** `purpose: 'preview'` (MODES, 24.10.2026) : aperçu fidèle de validation, simulé et partagé avec des données de démonstration, jamais livré. */
export interface FaithfulReference { importRef: string; pieceRef: string; sha256: string; publicLibraries: boolean; purpose?: 'delivery' | 'preview'; }
export interface FaithfulScope { kind: 'simulation' | 'module'; id: string; }
export interface FaithfulState { revision: number; entries: Record<string, string>; bytes: number; }
export interface FaithfulPatch { set: Record<string, string>; remove: string[]; }
export interface FaithfulStateRepository {
  persistent: boolean;
  get(scope: FaithfulScope): Promise<FaithfulState>;
  put(scope: FaithfulScope, input: FaithfulPatch & { baseRevision: number }): Promise<{ revision: number }>;
}
export const FAITHFUL_MESSAGE = 'geode.faithful.storage';
export function validFaithfulReference(value: FaithfulReference): boolean {
  const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuid.test(value.importRef) && uuid.test(value.pieceRef) && /^[0-9a-f]{64}$/i.test(value.sha256) && typeof value.publicLibraries === 'boolean';
}
export const FAITHFUL_LIMITS = { key: 256, value: 512 * 1024, keys: 1000, total: 5 * 1024 * 1024, body: 6 * 1024 * 1024 } as const;
export const utf8Bytes = (value: string): number => new TextEncoder().encode(value).length;
const record = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value) && [Object.prototype, null].includes(Object.getPrototypeOf(value));
export function validEntries(value: unknown): value is Record<string, string> {
  if (!record(value) || Object.keys(value).length > FAITHFUL_LIMITS.keys) return false;
  let total = 0;
  return Object.entries(value).every(([key, item]) => {
    if (key.length > FAITHFUL_LIMITS.key || typeof item !== 'string' || utf8Bytes(item) > FAITHFUL_LIMITS.value) return false;
    total += utf8Bytes(key) + utf8Bytes(item);
    return total <= FAITHFUL_LIMITS.total;
  });
}
export function readFaithfulMessage(event: MessageEvent, source: Window | null): FaithfulPatch | null {
  if (!source || event.source !== source || event.origin !== 'null') return null;
  const data: unknown = event.data;
  if (!record(data) || Object.keys(data).sort().join(',') !== 'remove,set,type' || data.type !== FAITHFUL_MESSAGE || !validEntries(data.set) || !Array.isArray(data.remove)) return null;
  if (data.remove.length > FAITHFUL_LIMITS.keys || !data.remove.every(key => typeof key === 'string' && key.length <= FAITHFUL_LIMITS.key) || new Set(data.remove).size !== data.remove.length) return null;
  if (data.remove.some(key => Object.prototype.hasOwnProperty.call(data.set, key))) return null;
  if (utf8Bytes(JSON.stringify(data)) > FAITHFUL_LIMITS.body) return null;
  return { set: data.set, remove: data.remove };
}
export function applyFaithfulPatch(entries: Record<string, string>, patch: FaithfulPatch): Record<string, string> {
  const next = Object.assign(Object.create(null), entries);
  patch.remove.forEach(key => { delete next[key]; });
  Object.assign(next, patch.set);
  if (!validEntries(next)) throw new DOMException('Storage quota exceeded', 'QuotaExceededError');
  return next;
}
